import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Languages, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgePill } from "@/components/Brand";

export default function Scripture() {
  const [params, setParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const book = params.get("book") || "John";
  const chapter = parseInt(params.get("chapter") || "1", 10);

  useEffect(() => {
    api.get("/scripture/books").then(({ data }) => setBooks(data.books));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/scripture/chapter", { params: { book, chapter } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [book, chapter]);

  const current = useMemo(() => books.find((b) => b.name === book), [books, book]);
  const ot = books.filter((b) => b.testament === "OT");
  const nt = books.filter((b) => b.testament === "NT");
  const chapters = current ? Array.from({ length: current.chapters }, (_, i) => i + 1) : [];

  const go = (b, c) => setParams({ book: b, chapter: String(c) });

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F3D66] sm:text-3xl">Scripture Reader</h1>
        <p className="text-sm text-[#6B6763]">English beside the original Hebrew &amp; Greek. Tap any original word for its meaning.</p>
      </div>

      {/* Book + chapter pickers */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Select value={book} onValueChange={(v) => go(v, 1)}>
          <SelectTrigger data-testid="book-select" className="w-[220px] rounded-full border-[#E8E4DF] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">
            <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[#9c9791]">Old Testament</div>
            {ot.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
            <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[#9c9791]">New Testament</div>
            {nt.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={String(chapter)} onValueChange={(v) => go(book, Number(v))}>
          <SelectTrigger data-testid="chapter-select" className="w-[130px] rounded-full border-[#E8E4DF] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">
            {chapters.map((c) => <SelectItem key={c} value={String(c)}>Chapter {c}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="ml-auto flex gap-1.5">
          <button data-testid="prev-chapter" disabled={chapter <= 1} onClick={() => go(book, chapter - 1)}
            className="grid h-9 w-9 place-items-center rounded-full border border-[#E8E4DF] bg-white text-[#6B6763] disabled:opacity-40 hover:text-[#F36353]"><ChevronLeft size={18} /></button>
          <button data-testid="next-chapter" disabled={current && chapter >= current.chapters} onClick={() => go(book, chapter + 1)}
            className="grid h-9 w-9 place-items-center rounded-full border border-[#E8E4DF] bg-white text-[#6B6763] disabled:opacity-40 hover:text-[#F36353]"><ChevronRight size={18} /></button>
        </div>
      </div>

      {loading && <div className="py-16 text-center text-[#6B6763]">Loading chapter…</div>}

      {!loading && data && (
        <div className="bp-fade-up rounded-3xl border border-[#E8E4DF] bg-white p-5 bp-card-shadow sm:p-7" data-testid="passage-view">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <BadgePill tone="primary"><BookOpen size={12} /> {data.book} {data.chapter}</BadgePill>
            <BadgePill tone="sage"><Languages size={12} /> {data.language} · {data.testament}</BadgePill>
          </div>

          <div className="space-y-6">
            {data.verses.map((v) => (
              <div key={v.verse} className="border-b border-[#F0EBE4] pb-6 last:border-0" data-testid={`verse-${v.verse}`}>
                {v.words && v.words.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-x-2 gap-y-1"
                    dir={data.language === "Hebrew" ? "rtl" : "ltr"}
                    style={{ fontSize: "1.5rem", lineHeight: 1.9 }}>
                    {v.words.map((w, i) => (
                      <Popover key={i}>
                        <PopoverTrigger asChild>
                          <button data-testid={`word-${v.verse}-${i}`}
                            className="rounded-lg px-1 text-[#0F3D66] transition-colors hover:bg-[#F36353]/10 hover:text-[#D94A3D]"
                            style={{ fontFamily: data.language === "Hebrew" ? "'Noto Serif Hebrew', serif" : "Georgia, serif" }}>
                            {w.original}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 rounded-2xl border-[#E8E4DF]">
                          <div className="text-xl font-semibold text-[#0F3D66]" style={{ fontFamily: data.language === "Hebrew" ? "'Noto Serif Hebrew', serif" : "Georgia, serif" }}>{w.original}</div>
                          {w.translit && <div className="text-sm italic text-[#748C70]">{w.translit}</div>}
                          {w.gloss && <div className="mt-1 text-sm text-[#0F3D66]">“{w.gloss}”</div>}
                          {w.strongs && <div className="mt-2"><BadgePill tone="muted">Strong's {w.strongs}</BadgePill></div>}
                        </PopoverContent>
                      </Popover>
                    ))}
                  </div>
                )}
                <p className="font-scripture text-lg leading-relaxed text-[#3b3a38]">
                  <sup className="mr-1 text-xs font-semibold text-[#F36353]">{v.verse}</sup>{v.english}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-[#9c9791]" data-testid="scripture-attribution">
            Berean Standard Bible and Berean Interlinear Bible, public domain.
          </p>
        </div>
      )}

      {!loading && !data && (
        <div className="rounded-2xl border border-dashed border-[#E8E4DF] bg-white p-10 text-center text-sm text-[#6B6763]">
          This chapter isn't available.
        </div>
      )}
    </div>
  );
}
